import { useMemo, useState } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { connect } from 'react-redux';

import {
  workflowAddDataFieldsMapping,
  workflowRemoveDataFieldsMapping,
  workflowSetDataFieldsMapping,
} from '~/actions/workflow';
import {
  customSettingsKeys,
  fieldTypesToDataFieldTypes,
} from '~/common/constants/workflow';
import DataFieldSelect from '~/components/Client/DataFieldSelect';
import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { Text } from '~/eds/src/components/Text/Text';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useDataFieldSection } from '~/hooks/useDataFieldSection';
import { getFieldErrors, getFieldOptions } from '~/reducers/workflow';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { Uuid } from '~/types/Uuid';
import { Workflow, WorkflowField } from '~/types/Workflow';
import { renameKey } from '~/utils/object';

import { filterOutDocumentPartFileFields } from './DataFieldsMappingUtil';
import { FieldSelect } from './FieldSelect';

interface DataFieldsMappingProps {
  dataFieldsMapping: Record<Uuid, PilotId>;
  errors: {
    count: number;
    invalidFieldMappingIds: Uuid[];
  };
  fields: Record<Uuid, WorkflowField>;
  shouldValidate: boolean;
  workflow: Workflow;
  workflowAddDataFieldsMapping: () => void;
  workflowRemoveDataFieldsMapping: (fieldId: Uuid) => void;
  workflowSetDataFieldsMapping: (mapping: Record<Uuid, PilotId>) => void;
}

type FieldMapType = keyof typeof fieldTypesToDataFieldTypes;

function DataFieldsMappingComponent({
  // connected
  dataFieldsMapping,
  errors,
  fields,
  shouldValidate,
  workflow,
  workflowAddDataFieldsMapping,
  workflowRemoveDataFieldsMapping,
  workflowSetDataFieldsMapping,
}: DataFieldsMappingProps) {
  const intl = useIntl();
  const [editingFieldId, setEditingFieldId] = useState<Nullable<Uuid>>(null);
  const { allDataFields, isFetching } = useDataFieldSection();

  const usedFieldIds = Object.keys(dataFieldsMapping).filter(
    (fieldId) => fieldId,
  );
  const usedDataFieldIds = Object.values(dataFieldsMapping).filter(
    (dataFieldId) => dataFieldId,
  );
  // fields without valid mapping to data field type are considered invalid
  const invalidFieldIds = Object.values(fields)

    .filter(
      (field) =>
        fieldTypesToDataFieldTypes?.[field?.type as FieldMapType]?.length === 0,
    )
    .map((field) => field.id);

  const isMultiDocEnabled = useFlag(
    FlagType.MultiDocWorkflowSupportAppendDocumentToContract,
  );
  const IS_DOCUMENT_PART_KEY =
    customSettingsKeys[WorkflowFieldType.File].IS_DOCUMENT_PART;

  function handleUpdateField(fieldId: Uuid, updatedFieldId: Uuid) {
    const updatedDataFieldsMapping = renameKey(
      fieldId,
      updatedFieldId,
      dataFieldsMapping,
    ) as Record<Uuid, PilotId>;
    workflowSetDataFieldsMapping(updatedDataFieldsMapping);
  }

  function handleUpdateDataField(fieldId: Uuid, updatedDataFieldId: Uuid) {
    const updatedDataFieldsMapping = {
      ...dataFieldsMapping,
      [fieldId]: updatedDataFieldId,
    } as Record<Uuid, PilotId>;
    workflowSetDataFieldsMapping(updatedDataFieldsMapping);
  }

  const onAddDataFieldsMapping = () => {
    setEditingFieldId('newField');
    workflowAddDataFieldsMapping?.();
  };

  const onRemoveDataFieldsMapping = (fieldId: Uuid) => {
    setEditingFieldId(null);
    workflowRemoveDataFieldsMapping?.(fieldId);
  };

  const fieldOptions = useMemo(() => {
    const allOptions = getFieldOptions(workflow, true);

    return filterOutDocumentPartFileFields(
      allOptions,
      fields,
      isMultiDocEnabled,
      IS_DOCUMENT_PART_KEY,
    );
  }, [workflow, fields, isMultiDocEnabled]);

  const isImportedDataField = (dataFieldId: number) => {
    return dataFieldId === 0;
  };

  const getDataFieldText = (dataFieldId: number) => {
    if (isImportedDataField(dataFieldId)) {
      return intl.formatMessage({
        id: 'DataFieldsMapping.notFoundText',
        defaultMessage: 'Not found',
        description: 'Text shown when data field is not found',
      });
    }
    const dataField = allDataFields[dataFieldId];
    if (!dataField) {
      return intl.formatMessage({
        id: 'DataFieldsMapping.naText',
        defaultMessage: 'N/A',
        description: 'Text shown when data field is not available',
      });
    }
    return dataField.label;
  };

  function renderFields() {
    return Object.keys(dataFieldsMapping).length ? (
      <Layout direction="column" spacing={6}>
        {Object.entries(dataFieldsMapping).map(
          ([fieldId, dataFieldId], index) => {
            const fieldIdValue = fieldId === 'null' ? null : fieldId;
            const isEditing =
              editingFieldId === fieldIdValue ||
              (editingFieldId === 'newField' &&
                index === Object.keys(dataFieldsMapping).length - 1);
            const hasError = errors.invalidFieldMappingIds.includes(fieldId);
            return (
              <Layout key={fieldId} align="center" justify="space-between">
                <Layout align="center" spacing={4} maxW={880}>
                  <Text minW={60} variant="body">
                    <FormattedMessage
                      id="DataFieldsMapping.mapFieldText"
                      defaultMessage="Map field"
                      description="Text for mapping a field"
                    />
                  </Text>
                  {isEditing ? (
                    <FieldSelect
                      filteredValues={[...usedFieldIds, ...invalidFieldIds]}
                      id={`workflow--settings-data-mapping-field-${index + 1}`}
                      name={`Select Field ${index + 1}`}
                      options={fieldOptions}
                      placeholder={intl.formatMessage({
                        id: 'DataFieldsMapping.pickTicketFieldPlaceholder',
                        defaultMessage: 'Pick a ticket field',
                        description: 'Placeholder for ticket field selection',
                      })}
                      shouldValidate={shouldValidate}
                      value={fieldIdValue}
                      onChange={(updatedFieldId) => {
                        setEditingFieldId(updatedFieldId);
                        handleUpdateField(fieldId, updatedFieldId || 'null');
                      }}
                    />
                  ) : (
                    <TruncateText variant="body-bold" maxW={350}>
                      {fields[fieldId]?.name ||
                        intl.formatMessage({
                          id: 'DataFieldsMapping.naText',
                          defaultMessage: 'N/A',
                          description:
                            'Text shown when data field is not available',
                        })}
                    </TruncateText>
                  )}
                  <Text minW={50} variant="body">
                    <FormattedMessage
                      id="DataFieldsMapping.toFieldText"
                      defaultMessage="to field"
                      description="Text connecting field mapping"
                    />
                  </Text>
                  {isEditing ? (
                    <DataFieldSelect
                      blacklistValues={usedDataFieldIds}
                      id={`workflow--settings-data-mapping-data-field-${
                        index + 1
                      }`}
                      fieldTypes={
                        fieldTypesToDataFieldTypes?.[
                          fields[fieldId]?.type as FieldMapType
                        ]
                      }
                      placeholder={intl.formatMessage({
                        id: 'DataFieldsMapping.pickDataFieldPlaceholder',
                        defaultMessage: 'Pick a data field',
                        description: 'Placeholder for data field selection',
                      })}
                      value={dataFieldId}
                      onChange={(updatedDataFieldId: Uuid) =>
                        handleUpdateDataField(fieldId, updatedDataFieldId)
                      }
                    />
                  ) : (
                    <TruncateText variant="body-bold" maxW={350}>
                      {getDataFieldText(dataFieldId)}
                    </TruncateText>
                  )}
                  <Text minW={100} variant="body">
                    <FormattedMessage
                      id="DataFieldsMapping.inPlatformText"
                      defaultMessage="in the platform"
                      description="Text indicating platform location"
                    />
                  </Text>
                </Layout>
                <Layout spacing={4}>
                  {hasError && (
                    <Icon
                      icon="status-warning"
                      color="status.warning"
                      tooltip={
                        isImportedDataField(dataFieldId)
                          ? intl.formatMessage({
                              id: 'DataFieldsMapping.mappingNotFoundTooltip',
                              defaultMessage: 'Mapping not found',
                              description: 'Tooltip when mapping is not found',
                            })
                          : intl.formatMessage({
                              id: 'DataFieldsMapping.incompatibleDataTooltip',
                              defaultMessage:
                                'The mapped data is incompatible with the field type.',
                              description:
                                'Tooltip when data is incompatible with field type',
                            })
                      }
                    />
                  )}
                  {isEditing ? (
                    <Icon
                      icon="check"
                      onClick={() => setEditingFieldId(null)}
                      color="status.info"
                    />
                  ) : (
                    <Icon
                      icon="edit"
                      onClick={() => setEditingFieldId(fieldIdValue)}
                      color="status.info"
                    />
                  )}
                  <Icon
                    icon="trash"
                    onClick={() => onRemoveDataFieldsMapping(fieldId)}
                    color="status.info"
                  />
                </Layout>
              </Layout>
            );
          },
        )}
      </Layout>
    ) : null;
  }

  const disableAddMapping = Object.keys(dataFieldsMapping).some(
    (fieldId) => fieldId === 'null',
  );

  return (
    <ContentContainer
      loadingContent={{
        isLoading: isFetching,
        message: intl.formatMessage({
          id: 'DataFieldsMapping.loadingMessage',
          defaultMessage: 'Loading field mapping',
          description: 'Loading message for field mapping',
        }),
      }}
    >
      <Layout direction="column">
        <Layout direction="row" spacing={2} align="center">
          <Icon icon="field" size="l" color="status.active" />
          <Text variant="section">
            <FormattedMessage
              id="DataFieldsMapping.sectionTitle"
              defaultMessage="Data Fields Mapping"
              description="Title for data fields mapping section"
            />
          </Text>
        </Layout>
        <Text variant="body" color="text.quiet" mb={4} mt={2}>
          <FormattedMessage
            id="DataFieldsMapping.sectionDescription"
            defaultMessage="Map specific fields from the ticket to data fields in the Evisort platform."
            description="Description for data fields mapping section"
          />
        </Text>
        {renderFields()}
        <Layout mt={6}>
          <Button
            disabled={disableAddMapping}
            iconPosition="left"
            icon="plus"
            id="workflow--data-mapping-add"
            text={intl.formatMessage({
              id: 'DataFieldsMapping.addMappingButton',
              defaultMessage: 'Add Mapping',
              description: 'Button text for adding a mapping',
            })}
            variant="secondary"
            onClick={() => onAddDataFieldsMapping()}
          />
        </Layout>
      </Layout>
    </ContentContainer>
  );
}

function mapStateToProps(
  {
    workflow,
    builder,
  }: {
    workflow: any;
    builder: any;
  },
  { intl }: { intl: IntlShape },
): {
  dataFieldsMapping: Record<Uuid, PilotId>;
  fields: Record<Uuid, WorkflowField>;
  workflow: Workflow;
  shouldValidate: boolean;
  errors: {
    count: number;
    invalidFieldMappingIds: Uuid[];
  };
} {
  return {
    dataFieldsMapping: workflow.settings.dataFieldsMapping,
    fields: workflow.fields,
    workflow,
    shouldValidate: builder.shouldValidate,
    errors: getFieldErrors(workflow, builder, intl),
  };
}

const _DataFieldsMapping = connect(mapStateToProps, {
  workflowAddDataFieldsMapping,
  workflowRemoveDataFieldsMapping,
  workflowSetDataFieldsMapping,
})(DataFieldsMappingComponent);

const DataFieldsMapping = injectIntl(_DataFieldsMapping as any);
export { DataFieldsMapping };
